import React from 'react';
import gql from 'graphql-tag';
import { isEqual, times } from 'lodash';
import track from 'react-tracking';
import { withRouter, WithRouterProps } from 'react-router';

import bind from '../bind';
import ListingCard from './listing_card';
import { ListingRowCard, PlaceholderCard as PlaceholderListingRowCard } from './listing_row_card';
import OverflowingRow from './overflowing_row';
import Tiles from './tiles';
import { CoreAdBannerWrapper, ListingsCollection as ListingsCollectionQuery } from '../rql';
import { trackEvent } from '../elog/mparticle_tracker';
import { MParticleEvent, MParticleEventName } from '../elog/mparticle_types';
import { IUserContext, withUserContext } from './user_context_provider';
import { AddToCartButtonFragment } from './add_to_cart_button';
import { AdCard } from './ads/ad_card';
import { WatchBadgeFragment } from './watch_badge';
import { isHomePage } from '../url_helpers';
import { ListingGreatValueFragment } from '../listing_great_value';
import { ListingCreateOfferButtonFragment } from '../offers/create_offer_button';
import { connect } from '../connect';
import { RCGrid } from '@reverbdotcom/cadence/components';
import { ThemeType } from '@reverbdotcom/cadence/components/types';
import { ThemeContext } from './theme_context_provider';

function componentNameForAd(
  componentName: string,
  listingsCollectionTitle: string,
): string {
  return `${componentName}-${listingsCollectionTitle}`;
}

export interface ExternalProps {
  callToAction?: JSX.Element;
  componentName?: string;
  largeTiles?: boolean;
  listings?: ListingsCollectionQuery.Fragment[];
  sponsoredListings?: ListingsCollectionQuery.Fragment[];
  loading?: boolean;
  maxCount?: number;
  title?: JSX.Element | string;
  subtitle?: string;
  subtitleHtml?: string;
  ledeHtml?: string;
  emptyState?: JSX.Element;
  jumplinkSlug?: string;
  onOfferSuccess?: Function;
  displayAsGrid?: boolean;
  displayAsRowCards?: boolean;
  hideMetadataAsOffer?: boolean;
  displayAddToCart?: boolean;
  listingsCount?: number;
  mParticleView?: MParticleEvent;
  showEstimatedMonthlyPayment?: boolean;
  ad?: CoreAdBannerWrapper.Ad;
  titleString?: string;
  showFlags?: boolean;
  shouldDisplayAsLanding?: boolean;
  cspFeaturedListingId?: string;
  displayGreatValue?: boolean;
  withSidebar?: boolean;
}

export type IProps = ExternalProps & IUserContext & WithRouterProps;

export class ListingsCollection extends React.Component<IProps, null> {
  static defaultProps: Partial<IProps> = {
    maxCount: 12,
    displayAsGrid: false,
    largeTiles: false,
    displayGreatValue: false,
  };

  static contextType = ThemeContext;

  static fragments = {
    listing: gql`
      fragment ListingsCollection on Listing {
        _id
        id
        bumped
        categoryUuids
        slug
        title
        description
        listingType
        condition {
          displayName
          conditionSlug
        }
        price {
          amountCents
          display
        }
        pricing {
          buyerPrice {
            display
            currency
            amountCents
          }
          originalPrice {
            display
          }
          ribbon {
            display
          }
        }
        images(input: {
          transform: "card_square",
          count: 3,
          scope: "photos",
          type: "Product"
        }) {
          source
        }
        watching
        state
        stateDescription
        shipping {
          shippingPrices {
            shippingMethod
            carrierCalculated
            destinationPostalCodeNeeded
            rate {
              amount
              amountCents
              currency
              display
            }
          }
          freeExpeditedShipping
          localPickupOnly
        },
        shop {
          _id
          address {
            country {
              countryCode
              name
            }
          }
        }
        ...AddToCartButtonFields
        ...WatchBadgeData
        ...ListingGreatValueData
        ...ListingCreateOfferButtonData
      }
      ${WatchBadgeFragment}
      ${AddToCartButtonFragment}
      ${ListingGreatValueFragment}
      ${ListingCreateOfferButtonFragment}
    `,
    bumpKey: gql`
      fragment BumpKey on Listing {
        _id
        bumpKey {
          key
        }
      }
    `,
    financingFields: gql`
      fragment FinancingFields on Listing {
        _id
        estimatedMonthlyPaymentPlan {
          estimatedMonthlyPayment {
            display
          }
        }
      }
    `,
    shopFields: gql`
      fragment ShopFields on Listing {
        _id
        shop {
          _id
          address {
            locality
            countryCode
            region
          }
        }
      }
    `,
  };

  componentDidMount() {
    if (!this.props.loading && this.listings?.length) {
      this.trackView();
      this.trackSponsoredImpressions();
    }
  }

  componentDidUpdate(oldProps) {
    if (this.propsDidChange(oldProps)) {
      this.trackView();
      this.trackSponsoredImpressions();
    }
  }

  trackView() {
    const { mParticleView } = this.props;
    if (!mParticleView) { return; }

    trackEvent(mParticleView);
  }

  sponsoredListings() {
    return this.props.sponsoredListings ?? [];
  }

  trackSponsoredImpressions() {
    const sponsoredListings = this.sponsoredListings();
    const bumpedListings = sponsoredListings.filter(l => l.bumped);

    if (!sponsoredListings.length) { return; }

    trackEvent({
      eventName: MParticleEventName.ListingImpression,
      listingIds: sponsoredListings.map(l => l.id),
      bumpedListingIds: bumpedListings.map(l => l.id),
      listings: sponsoredListings,
      shownAsBumped: true,
      componentName: this.props.componentName,
    });
  }

  propsDidChange(oldProps) {
    return !this.props.loading
      && this.props.listings?.length
      && !isEqual(this.props.listings, oldProps.listings);
  }

  get listings() {
    return (this.props.sponsoredListings || []).concat(this.props.listings || []);
  }

  shouldBeSingleRow() {
    if (this.props.displayAsRowCards) return false;
    return !this.props.displayAsGrid;
  }

  isEmpty() {
    return !this.props.loading && !this.listings?.length;
  }

  showGreatValueForHomePage() {
    if (!this.props.location) return false;

    return isHomePage(this.props.location);
  }

  @bind
  renderListingCard(listing: ListingsCollectionQuery.Fragment, idx) {
    // For tracking purposes, adjust sponsored rows positions start at 0.
    const sponsored = this.props.sponsoredListings ?? [];
    const isSponsored = sponsored.find(sponsoredListing => sponsoredListing.id == listing.id);
    const adjustedIdx = isSponsored ? idx : idx - sponsored.length;

    if (this.props.displayAsRowCards) {
      return (
        <ListingRowCard
          key={listing.id}
          listing={listing}
          position={adjustedIdx}
          onOfferSuccess={this.props.onOfferSuccess}
          hideMetadataAsOffer={this.props.hideMetadataAsOffer}
          listingsCount={this.props.listingsCount}
          showEstimatedMonthlyPayment={this.props.showEstimatedMonthlyPayment}
          componentName={this.props.componentName}
          cspFeaturedListingId={this.props.cspFeaturedListingId}
          displayGreatValue={this.props.displayGreatValue}
        />
      );
    }

    return (
      <ListingCard
        key={listing.id}
        listing={listing}
        position={idx}
        hideMetadataAsOffer={this.props.hideMetadataAsOffer}
        displayAddToCart={this.props.displayAddToCart}
        listingsCount={this.props.listingsCount}
        trackingName={this.props.componentName}
        showFlag={this.props.showFlags}
        watchBadgeParentComponentName={this.props.componentName}
        displayGreatValue={this.showGreatValueForHomePage()}
      />
    );
  }

  renderPlaceholder() {
    if (this.props.displayAsRowCards) {
      return <PlaceholderListingRowCard />;
    }
    return <ListingCard />;
  }

  renderAd() {
    if (!this.props.ad) return false;
    if (this.props.displayAsRowCards) return false;
    return (
      <AdCard
        ad={this.props.ad}
        key={this.props.ad.uuid}
        componentName={
          componentNameForAd(
            this.props.componentName,
            this.props.titleString,
          )
        }
      />
    );
  }

  renderTiles() {
    return (
      <Tiles
        singleRow={this.shouldBeSingleRow()}
        placeholder={this.renderPlaceholder()}
        expectedCount={this.props.maxCount}
        loading={this.props.loading}
        largeTiles={this.props.largeTiles && !this.props.displayAsRowCards}
        oneWide={this.props.displayAsRowCards}
        emptyState={this.props.emptyState}
      >
        {this.renderAd()}
        {this.listings.map(this.renderListingCard)}
      </Tiles>
    );
  }

  gridColSpan() {
    if (this.props.displayAsRowCards) {
      return { desktop: 12 };
    }
    return { desktop: 2, tablet: 3, mobile: 5 };
  }

  renderGridItems() {
    if (this.props.loading && !this.listings?.length) {
      return this.gridPlaceholders();
    }
    return (
      <>
        {this.renderAd() &&
          <RCGrid.Item
            colSpan={this.gridColSpan()}
            key={this.props.ad.uuid}
          >
            {this.renderAd()}
          </RCGrid.Item>
        }
        {this.listings.map((listing, index) => {
          return (
            <RCGrid.Item
              colSpan={this.gridColSpan()}
              key={listing.id}
            >
              {this.renderListingCard(listing, index)}
            </RCGrid.Item>
          );
        })}
      </>
    );
  }

  gridPlaceholders() {
    return times(this.props.maxCount, index => (
      <RCGrid.Item
        colSpan={this.gridColSpan()}
        key={index}
      >
        {this.props.displayAsRowCards
          ? <PlaceholderListingRowCard />
          : <ListingCard />
        }
      </RCGrid.Item>
    ));
  }

  render() {
    if (this.isEmpty() && !this.props.emptyState) {
      return null;
    }

    const useRCListingGrid = this.context?.theme === ThemeType.web2 && !this.props.withSidebar;

    return (
      <OverflowingRow
        title={this.props.title}
        subtitle={this.props.subtitle}
        subtitleHtml={this.props.subtitleHtml}
        ledeHtml={this.props.ledeHtml}
        action={!this.isEmpty() && this.props.callToAction}
        collectionCount={this.listings?.length}
        appendAction={this.props.displayAsGrid || this.props.displayAsRowCards}
        id={this.props.jumplinkSlug}
        shouldDisplayAsLanding={this.props.shouldDisplayAsLanding}
      >
        {useRCListingGrid ? (
          <RCGrid
            disableAndPulse={this.props.loading}
            singleRowOn={this.shouldBeSingleRow() ? 'all' : null}
            flexboxItems
          >
            {this.renderGridItems()}
          </RCGrid>
        ) : this.renderTiles()
        }
      </OverflowingRow>
    );
  }
}

// TODO - set componentName in tracking context more directly where we actually need it
const withTrackingListingsCollection = track((props: IProps) => ({ componentName: props.componentName }))(ListingsCollection);

export default connect<ExternalProps>([
  withUserContext,
  withRouter,
])(withTrackingListingsCollection);
